import React from 'react'
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, UniqueIdentifier, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

interface Props<T> {
  items: T[]
  idField: keyof T
  onChange: (oldIndex: number, newIndex: number, nextItems: T[]) => void
  children: React.ReactNode
}

/**
 * @param items
 * @param idField
 * @param onChange
 * @param children
 * @constructor
 */
export const TableSortable = <T extends Object>({ items, idField, onChange, children }: Props<T>) => {
  const [activeId, setActiveId] = React.useState(null)
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const ids = React.useMemo((): UniqueIdentifier[] => {
    return items.map(i => (i[idField] as unknown as UniqueIdentifier))
  }, [items, idField])

  const handleDragStart = React.useCallback((event: any) => {
    const { active } = event
    setActiveId(active.id)
  }, [])

  const handleDragEnd = React.useCallback((event: any) => {
    const { active, over } = event
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item[idField] === active.id)
      const newIndex = items.findIndex((item) => item[idField] === over.id)
      const nextItems = arrayMove(items, oldIndex, newIndex)
      onChange(oldIndex, newIndex, nextItems)
    }
    setActiveId(null)
  }, [items, idField, onChange])

  const handleDragCancel = React.useCallback(() => {
    setActiveId(null)
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
    >
      <SortableContext
        items={ids}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}
